import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { IncidentSummary } from '../Types/Incident';
import { getIncidentSummary, getIncidentDetail } from '../Services/IncidentService';
import { getLocations } from '../Services/LocationService';
import { appColors } from '../Config/Theme';
import AppBreadcrumbs from '../Components/Navigation/AppBreadcrumbs';
import IncidentInfoBar from '../Components/Incidents/IncidentInfoBar';
import ClosedIncidentBanner from '../Components/Incidents/ClosedIncidentBanner';
import LoadingSpinner from '../Components/Elements/LoadingSpinner';
import { isIncidentClosed, canEditClosedRoster } from '../Utils/incidentHelpers';
import { useSecurity } from '../Contexts/SecurityProvider';
import { useIncidentAssignments } from '../Hooks/useIncidentAssignments';
import { getFacilityMapForLocation, type FacilityMapFile } from '../features/facilityMaps/facilityMapRegistry';

const FacilityMapPage: React.FC = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const navigate = useNavigate();
  const { hasAnyRole, security } = useSecurity();

  const [summary, setSummary] = useState<IncidentSummary | null>(null);
  const [locationAddress, setLocationAddress] = useState<string | null>(null);
  const [selectedMapIdx, setSelectedMapIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!incidentId) return;
    setLoading(true);
    setError(null);
    try {
      const [sumResult, detail, locResult] = await Promise.all([
        getIncidentSummary(incidentId),
        getIncidentDetail(incidentId).catch(() => null),
        getLocations().catch(() => ({ locations: [], isFallback: true })),
      ]);
      const result = sumResult.summary;
      // Summary may lack fields — fall back to incident detail
      if (!result.incidentName && detail?.incidentName) {
        result.incidentName = detail.incidentName;
      }
      if (!result.location && detail?.location) {
        result.location = detail.location;
      }
      if (!result.incidentType && detail?.incidentType) {
        result.incidentType = detail.incidentType;
      }
      if (!result.startedAtUtc && detail?.startedAtUtc) {
        result.startedAtUtc = detail.startedAtUtc;
      }
      if (!result.status && detail?.status) {
        result.status = detail.status;
      }
      setSummary(result);

      // Resolve location address from /api/locations
      const locName = result.location?.toLowerCase().trim();
      if (locName) {
        const loc = locResult.locations.find(l => l.locationName?.toLowerCase().trim() === locName);
        if (loc) {
          const parts = [loc.city, loc.stateProvince].filter(Boolean);
          if (parts.length) setLocationAddress(parts.join(', '));
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load incident.');
    } finally {
      setLoading(false);
    }
  }, [incidentId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isClosed = isIncidentClosed(summary);
  const { assignments } = useIncidentAssignments(isClosed ? incidentId : undefined);
  const closedRosterEditable = isClosed && canEditClosedRoster({
    hasAnyRole,
    currentUserId: security?.appUserId,
    assignments,
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <LoadingSpinner text="Loading incident..." />
      </Box>
    );
  }

  if (error || !summary) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Incident not found.'}</Alert>
      </Box>
    );
  }

  const map = getFacilityMapForLocation(summary.location);
  const activeMap: FacilityMapFile | undefined = map?.maps[selectedMapIdx] ?? map?.maps[0];

  return (
    <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
      {/* ── Breadcrumbs ── */}
      <AppBreadcrumbs
        items={[
          { label: 'Incidents', path: '/' },
          { label: summary.incidentName || 'Incident Dashboard', path: `/incidents/${incidentId}/incidentdashboard` },
          { label: 'Facility Map' },
        ]}
      />

      {/* ── Closed banner ── */}
      {isClosed && !closedRosterEditable && <ClosedIncidentBanner />}

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: appColors.text.heading }}>
            Facility Map
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.body, mt: 0.25 }}>
            View the evacuation/facility map for this incident location.
          </Typography>
        </Box>
      </Box>

      {/* ── Incident Info Bar ── */}
      {summary.incidentName && (
        <IncidentInfoBar
          incidentName={summary.incidentName}
          incidentType={summary.incidentType}
          location={summary.location}
          startedAtUtc={summary.startedAtUtc}
          status={summary.status}
        />
      )}

      {map && activeMap ? (
        <>
          {/* Map title & address */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 1.5,
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: appColors.text.heading }}>
                {map.title}
              </Typography>
              {locationAddress && (
                <Typography variant="body2" sx={{ color: appColors.text.caption }}>
                  {locationAddress}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon />}
                component="a"
                href={activeMap.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${activeMap.label} in a new tab`}
              >
                Open in New Tab
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                component="a"
                href={activeMap.pdfUrl}
                download
                aria-label={`Download ${activeMap.label}`}
              >
                Download
              </Button>
            </Box>
          </Box>

          {/* Map selector tabs (only when multiple maps) */}
          {map.maps.length > 1 && (
            <Tabs
              value={selectedMapIdx}
              onChange={(_e, v) => setSelectedMapIdx(v as number)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 1.5 }}
            >
              {map.maps.map((m, i) => (
                <Tab key={i} label={m.label} />
              ))}
            </Tabs>
          )}

          {/* Helper text */}
          <Typography variant="caption" sx={{ color: appColors.text.placeholder, mb: 1, display: 'block' }}>
            If the map does not display, open it in a new tab or download the PDF.
          </Typography>

          {/* Embedded PDF */}
          <Box
            sx={{
              border: `1px solid ${appColors.border?.light ?? '#e0e0e0'}`,
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <iframe
              title={`${activeMap.label} PDF`}
              src={activeMap.pdfUrl}
              style={{
                width: '100%',
                height: 'calc(100vh - 340px)',
                minHeight: 600,
                border: 0,
                display: 'block',
              }}
            />
          </Box>
        </>
      ) : (
        /* Empty state — no map for this location */
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            bgcolor: appColors.surface.card,
            borderRadius: 2,
            border: `1px solid ${appColors.border?.light ?? '#e0e0e0'}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: appColors.text.heading, mb: 1 }}>
            No facility map available
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.caption, mb: 3, maxWidth: 420, mx: 'auto' }}>
            No facility map is configured for <strong>{summary.location || 'this location'}</strong>.
            Facility maps can be added by configuring this location in the Web App map registry.
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/incidents/${incidentId}/incidentdashboard`)}
          >
            Back to Dashboard
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FacilityMapPage;
